import './FvtWelcome.scss';

import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { faCheck, faEnvelope, faFlask, faUsers } from '@fortawesome/pro-solid-svg-icons';

import { BridgeBioMaterialCode } from '@components/marketing/BridgeBioMaterialCode/BridgeBioMaterialCode';
import { ProcessBand, ProcessBandStep } from '@components/ProcessBand/ProcessBand';

import { AboutHypopara } from './components/AboutHypopara';
import { FaqSection } from './components/FaqSection';
import { Hero } from './components/Hero';
import { ProgramOverview } from './components/ProgramOverview';
import { StickyBottomCta } from './components/StickyBottomCta';
import { TalkToFamily } from './components/TalkToFamily';
import { WhatToExpect } from './components/WhatToExpect';

export const PROCESS_BAND_STEPS: ProcessBandStep[] = [
  {
    label: '01',
    title: 'Your report was shared',
    description: 'Your provider sent us your genetic testing report and asked us to follow up with your family.',
    state: 'done',
    icon: faCheck,
  },
  {
    label: "02 · You're here",
    title: 'Invite your relatives',
    description: 'Add eligible blood relatives so we can reach them.',
    state: 'current',
    icon: faUsers,
  },
  {
    label: '03',
    title: 'We email each one',
    description: 'Each relative gets a private invitation to get started. They choose what to share on their own time.',
    state: 'upcoming',
    icon: faEnvelope,
  },
  {
    label: '04',
    title: 'No-charge testing for them',
    description:
      'Eligible blood relatives are offered no-charge genetic testing and the opportunity to speak with a genetic counselor.',
    state: 'upcoming',
    icon: faFlask,
  },
];

const PROCESS_BAND_SECTION_ID = 'fvt-flow';

export const FvtWelcome = () => {
  const heroRef = useRef<HTMLElement | null>(null);
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setShowStickyCta(!entry.isIntersecting);
        });
      },
      { root: null, threshold: 0.1 },
    );

    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  return (
    <div className='fvt-welcome-page w-100'>
      <main className={classNames(showStickyCta && 'fvt-welcome-page__main--sticky-offset')}>
        <Hero heroRef={heroRef} learnMoreTargetId={PROCESS_BAND_SECTION_ID} />

        <Container fluid='xxl'>
          <ProcessBand
            heading='What happens next'
            id={PROCESS_BAND_SECTION_ID}
            introEyebrow='The flow, end to end'
            introLead="Here's the full picture. You're on step 2."
            steps={PROCESS_BAND_STEPS}
            variant='fvt'
          />
        </Container>

        <TalkToFamily />

        <AboutHypopara />

        <ProgramOverview />

        <WhatToExpect />

        <FaqSection />

        <Container fluid='xxl'>
          <div className='fvt-welcome-page__references'>
            <h2 className='fvt-welcome-page__references-heading'>REFERENCES</h2>
            <ol>
              <li>Roszko et al. 2022 DOI: 10.1002/jbmr.4659</li>
              <li>Mannstadt et al. 2026. DOI: 10.1093/ejendo/lvag096.133</li>
            </ol>
            <BridgeBioMaterialCode />
          </div>
        </Container>

        <StickyBottomCta visible={showStickyCta} />
      </main>
    </div>
  );
};
